import { motion } from 'framer-motion';
import { FaFilePdf, FaShoppingBag, FaGlobe, FaTrophy, FaBook, FaExternalLinkAlt } from 'react-icons/fa';
import Seo from '../components/Seo';

function Resources() {
  const clubDocs = [
    {
      title: 'Club Constitution',
      description: 'The official Marple Swimming Club Constitution outlines our club\'s governance, rules, and procedures.',
      icon: <FaFilePdf className="text-5xl" />,
      color: 'red',
      link: '/Marple-Swimming-Club-Constitution-2023.pdf',
      linkText: 'View Constitution (PDF)'
    }
  ];

  const externalLinks = [
    {
      title: 'Marple SC Club Shop',
      description: 'Official club merchandise including swimwear, training equipment, and club-branded items.',
      icon: <FaShoppingBag className="text-5xl" />,
      color: 'blue',
      link: 'https://pbteamwear.co.uk/collections/marple-sc',
      linkText: 'Visit Club Shop'
    },
    {
      title: 'British Swimming',
      description: 'The national governing body for swimming in Great Britain. Information about competitions, coaching, and development.',
      icon: <FaTrophy className="text-5xl" />,
      color: 'green',
      link: 'https://www.britishswimming.org',
      linkText: 'Visit British Swimming'
    },
    {
      title: 'Stockport Metro Swimming Club',
      description: 'Local swimming club and competition organizer. Information about Mini Meets and competitive opportunities.',
      icon: <FaGlobe className="text-5xl" />,
      color: 'purple',
      link: 'https://www.stockportmetro.co.uk',
      linkText: 'Visit Stockport Metro'
    },
    {
      title: 'Swim England',
      description: 'Official governing body for swimming in England. Resources for swimmers, parents, and coaches.',
      icon: <FaBook className="text-5xl" />,
      color: 'orange',
      link: 'https://www.swimming.org',
      linkText: 'Visit Swim England'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      red: { bg: 'from-red-50 to-red-100', border: 'border-red-500', icon: 'text-red-600', button: 'bg-red-600 hover:bg-red-700' },
      blue: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-500', icon: 'text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' },
      green: { bg: 'from-green-50 to-green-100', border: 'border-green-500', icon: 'text-green-600', button: 'bg-green-600 hover:bg-green-700' },
      purple: { bg: 'from-purple-50 to-purple-100', border: 'border-purple-500', icon: 'text-purple-600', button: 'bg-purple-600 hover:bg-purple-700' },
      orange: { bg: 'from-orange-50 to-orange-100', border: 'border-orange-500', icon: 'text-orange-600', button: 'bg-orange-600 hover:bg-orange-700' }
    };
    return colors[color];
  };

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Resources"
        description="Useful links and resources for Marple Swimming Club members, including Swim England, British Swimming and local pool information."
        path="/resources"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-6">Club Resources</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Access important club documents and useful external websites for swimmers,
            parents, and coaches.
          </p>
        </motion.div>

        {/* Club Documents */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-12">Club Documents</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {clubDocs.map((doc, index) => {
              const colors = getColorClasses(doc.color);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}
                >
                  <div className={`${colors.icon} mb-6`}>
                    {doc.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{doc.title}</h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {doc.description}
                  </p>
                  <a
                    href={doc.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 ${colors.button} text-white px-6 py-3 rounded-full font-bold transition-all duration-300 shadow-md`}
                  >
                    <FaFilePdf />
                    {doc.linkText}
                  </a>
                </motion.div>
              );
            })}

            {/* Coming Soon Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-300 rounded-2xl p-8 shadow-lg"
            >
              <div className="text-gray-400 mb-6">
                <FaBook className="text-5xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-4">Additional Documents</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Other important club documents including policies, procedures, and forms will be made available here as needed.
              </p>
              <p className="text-sm text-gray-500 italic">
                Check back regularly for updates and new documents.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* External Websites */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-12">External Websites</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {externalLinks.map((link, index) => {
              const colors = getColorClasses(link.color);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}
                >
                  <div className={`${colors.icon} mb-6`}>
                    {link.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{link.title}</h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {link.description}
                  </p>
                  <a
                    href={link.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 ${colors.button} text-white px-6 py-3 rounded-full font-bold transition-all duration-300 shadow-md`}
                  >
                    {link.linkText}
                    <FaExternalLinkAlt className="text-sm" />
                  </a>
                </motion.div>
              );
            })}
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default Resources;
